import { useMemo } from "react";
import { ApolloError, useQuery } from "@apollo/client";

import { AdventCampaign, AdventCampaignQuery } from "../api/types";
import { DoorMechanic, parseDoorMechanics } from "../lib/advent-door-mechanics";
import { ADVENT_CAMPAIGN } from "utility/graphQl/query";
import useFeatureFlags, { FEATURE_FLAGS } from "./use-feature-flags";

interface UseAdventCampaignReturn {
  /** The flag is known and on; the query only runs once this is true. */
  enabled: boolean;
  flagsResolved: boolean;
  campaign: AdventCampaign | null;
  /** Each door's parsed mechanic, keyed by door id. */
  mechanics: ReadonlyMap<string, DoorMechanic>;
  loading: boolean;
  error?: ApolloError;
}

const NO_MECHANICS: ReadonlyMap<string, DoorMechanic> = new Map();

export default function useAdventCampaign(): UseAdventCampaignReturn {
  const { isEnabled, resolved: flagsResolved } = useFeatureFlags();
  const enabled = flagsResolved && isEnabled(FEATURE_FLAGS.DECEMBER_DOORS);

  const { data, loading, error } = useQuery<AdventCampaignQuery>(
    ADVENT_CAMPAIGN,
    {
      fetchPolicy: "cache-and-network",
      skip: !enabled,
    }
  );

  const campaign = data?.adventCampaign ?? null;
  const mechanics = useMemo(
    () => (campaign ? parseDoorMechanics(campaign.doors) : NO_MECHANICS),
    [campaign]
  );

  return {
    enabled,
    flagsResolved,
    campaign,
    mechanics,
    loading,
    error,
  };
}
